import { useEffect, useRef } from "react";
import { useParams } from "next/navigation";
import type Appsignal from "@appsignal/javascript";

type CaughtError = Error & { digest?: string };

/**
 * Default reporter: the AppSignal instance instrumentation-client.ts installs
 * on `window.__appsignal` before hydration — the same one AppsignalProvider
 * wraps. It is reached through that global rather than React context so the
 * ROOT boundary, which renders outside every provider, can report too. No
 * instance (development, or the frontend key unset) means a no-op, and a
 * reporter failure is swallowed: nothing here may throw inside a boundary that
 * is already rendering because of a crash.
 */
function reportToAppsignal(error: CaughtError, siteId?: string): void {
  try {
    const appsignal = (window as unknown as { __appsignal?: Appsignal }).__appsignal;
    // sendError returns a promise or nothing; Promise.resolve normalizes both so a
    // rejected send is swallowed the same way a synchronous throw is.
    Promise.resolve(appsignal?.sendError(error, { siteId: siteId ?? "unknown" })).catch(() => {});
  } catch {
    // Never let reporting crash the error surface.
  }
}

/**
 * Reports an error caught by a Next.js App Router `error.tsx` to AppSignal.
 *
 * An `error.tsx` boundary sits INSIDE `AppsignalProvider` and swallows the
 * render error before that provider's own `ErrorBoundary` can see it, so
 * without this the crash would never be reported (and in development, where
 * AppSignal ships dark, the boundary is the only safety net at all). The ref —
 * not just the effect's dependency array — guards against React StrictMode's
 * dev-only mount→cleanup→mount double-invoke reporting the same error twice.
 *
 * The site tag comes from the `[siteId]` route param, matching the tag
 * AppsignalProvider puts on the crashes it does catch.
 *
 * `report` is a test seam: it defaults to the real reporter and every boundary
 * calls the hook with one argument; tests inject a fake to assert the dedup
 * and tagging contracts without a live AppSignal instance.
 *
 * The error is forwarded **by reference, unchanged**: AppSignal reads
 * `error.digest` off the object it is handed, so normalizing on the way
 * through (`new Error(error.message)`) would silently strip it.
 */
export default function useReportCaughtError(
  error: CaughtError,
  report: (error: CaughtError, siteId?: string) => void = reportToAppsignal,
): void {
  const params = useParams<{ siteId?: string }>();
  const siteId = params?.siteId;
  const reportedErrorRef = useRef<Error | null>(null);

  useEffect(() => {
    if (reportedErrorRef.current === error) return;
    reportedErrorRef.current = error;
    report(error, siteId);
  }, [error, report, siteId]);
}
